import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Container, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  IconButton,
  Tooltip,
  Box,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  useMediaQuery,
  useTheme,
  Avatar,
  FormControlLabel,
  Switch,
  Stack,
  MenuItem,
  Badge,
  Tabs,
  Tab,
  Drawer,
  Fab
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Check as CheckIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  FilterList as FilterListIcon,
  Person as PersonIcon,
  EmojiEvents as EmojiEventsIcon,
  Block as BlockIcon,
  Clear
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import SummaryCard from '../../components/common/SummaryCard';
import { useAuth } from '../../contexts/AuthContext';

import api from '../../services/api';
import StudentAwardsTab from '../../components/teacher/StudentAwardsTab';
import AddStudentDialog from '../../components/AddStudentDialog';
import { 
  SUBJECTS, 
  ACADEMIC_YEARS 
} from '../../config/Config';
import { filterPublished } from '../../utils/publishedFilter';
import { fetchUnifiedTeacherStudents } from '../../utils/teacherStudentRoster';
import {
  teacherFilterClearButtonSx,
  teacherFilterFieldSx,
  teacherFilterHeaderSx,
  teacherFilterPanelSx,
  teacherFilterTitleSx,
  teacherFilterTopStackSx,
} from '../../styles/teacherFilterStyles';

const drawerWidth = 280;

const StudentsPage = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  
  // Check if user is a parent
  const isParent = user?.isParent || user?.is_parent || (user?.role_name && user?.role_name.toString().toLowerCase() === 'parent');
  
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters,setFilters]=useState({year:'',subject:'',name:'',status:'',invitation_status:''});
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [filterOpen, setFilterOpen] = useState(false);

  const navigate = useNavigate();
  const handleBack = () => navigate(-1);
  
  // Track if initial fetch has been done to prevent double refresh
  const hasInitialFetch = useRef(false);

  // Fetch teacher's students (platform relationships + teacher-managed roster rows)
  const fetchStudents = useCallback(async (yearsData = null) => {
    try {
      setLoading(true);
      const list = await fetchUnifiedTeacherStudents(api);
      // attach academic year label using cached list
      // Use provided yearsData or fall back to state
      const yearsToUse = yearsData || academicYears;
      const yrsMap = new Map(yearsToUse.map(y=>[y.id, y.year]));
      setStudents(list.map(s=>({
        ...s,
        academic_year_label: yrsMap.get(s.academic_year_id) || ''
      })));
      setError('');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || `Failed to fetch ${isParent ? 'children' : 'students'}. Please try again.`;
      setError(errorMsg);
      console.error('Error fetching students:', err);
    } finally {
      setLoading(false);
    }
  }, [academicYears, isParent]);

  useEffect(() => {
    // Wait for auth context to resolve
    if (user === undefined) return;

    const init = async () => {
      try {
        const [subRes, yrRes] = await Promise.all([
          api.get(SUBJECTS),
          api.get(ACADEMIC_YEARS + '?published_only=true')
        ]);
        const subs = Array.isArray(subRes.data) ? subRes.data : (subRes.data.results || []);
        const yrs = Array.isArray(yrRes.data) ? yrRes.data : (yrRes.data.results || []);
        setSubjects(subs);
        setAcademicYears(filterPublished(yrs));

        // After static data is loaded, perform the initial fetch of students
        // Pass years data directly to avoid dependency on state
        await fetchStudents(yrs);
        hasInitialFetch.current = true; // Mark initial fetch as done
      } catch (e) {
        console.error('Meta fetch failed', e);
        setLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const clearFilters = () => {
    setFilters({ year: '', subject: '', name: '', status: '', invitation_status: '' });
  };

  // Handle edit student
  const handleEdit = (student) => {
    setEditingStudent(student);
    setOpenDialog(true);
  };

  // Open dialog for adding a new student
  const handleOpenDialog = () => {
    setEditingStudent(null);
    setOpenDialog(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setError('');
  };

  // Show snackbar notification
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Split subjects string into array
  const getSubjectChips = (subjName) => {
    if (!subjName) return [];
    return [subjName];
  };

  // Derived filtered list
  const filteredStudents = students.filter(s=>{
    const matchName = `${s.first_name} ${s.last_name} ${s.email}`.toLowerCase().includes(filters.name.toLowerCase());
    const matchYear = !filters.year || String(s.academic_year_id||'')===String(filters.year);
    const matchSubject = !filters.subject || String(s.subject_id||'')===String(filters.subject);
    
    // Handle status filter - "All Status" means show all students regardless of status
    const matchStatus = filters.status === '' ? true : // "All Status" - show all
      (filters.status === 'active' && s.is_active) || // Show active students
      (filters.status === 'inactive' && !s.is_active); // Show inactive students
    
    // Handle invitation status filter - "All Invitation Status" means show all students regardless of invitation status
    const matchInvitationStatus = filters.invitation_status === '' ? true : // "All Invitation Status" - show all
      s.invitation_status === filters.invitation_status; // Show students with specific invitation status
    
    return matchName && matchYear && matchSubject && matchStatus && matchInvitationStatus;
  });

  // Open / close filter drawer
const toggleFilter = (open) => () => setFilterOpen(open);

// Render student rows for desktop view
  const [awardsDialogOpen, setAwardsDialogOpen] = useState(false);
  const [selectedStudentForAwards, setSelectedStudentForAwards] = useState(null);

  const handleOpenAwardsDialog = (student) => {
    setSelectedStudentForAwards(student);
    setAwardsDialogOpen(true);
  };

  const handleCloseAwardsDialog = () => {
    // Refresh students to reflect updated award counts
    fetchStudents();
    setAwardsDialogOpen(false);
    setSelectedStudentForAwards(null);
  };

  // Extracted renderDesktopRows() with styling updates based on screenshot
const getSubjectColor = (subject) => {
  if (!subject) return 'default';
  const s = subject.toLowerCase();
  if (s === 'mathematics') return 'error';
  if (s === 'chemistry') return 'primary';
  return 'default';
};

const getColorFromName = (name) => {
  const colors = ['#BBDEFB', '#C8E6C9', '#F0F4C3', '#FFE0B2', '#D1C4E9'];
  let charCode = name?.charCodeAt(0) ?? 0;
  return colors[charCode % colors.length];
};

const getStatusLabel = (student) => {
  if (!student.is_active) {
    return 'Inactive';
  }
  return 'Active';
};

const getStatusColor = (student) => {
  if (!student.is_active) {
    return 'default';
  }
  return 'success';
};

const getInvitationStatusLabel = (invitationStatus) => {
  switch (invitationStatus) {
    case 'awaiting':
      return 'Awaiting';
    case 'yes':
      return 'Accepted';
    case 'no':
      return 'Rejected';
    case 'exited':
      return 'Exited';
    case 'rejected':
      return 'Rejected';
    case 'not_invited':
      return 'Not Invited';
    default:
      return 'Unknown';
  }
};

const getInvitationStatusColor = (invitationStatus) => {
  switch (invitationStatus) {
    case 'awaiting':
      return 'warning';
    case 'yes':
      return 'success';
    case 'no':
      return 'error';
    case 'exited':
      return 'default';
    case 'rejected':
      return 'error';
    case 'not_invited':
      return 'default';
    default:
      return 'default';
  }
};

const renderDesktopRows = () => {
  return (
    <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 980 }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: 'grey.50' }}>
            <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.875rem', py: 2 }}>
              Actions
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', py: 2 }}>
              Student
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', py: 2 }}>
              Subject
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', py: 2 }}>
              Joined Date
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', py: 2 }}>
              Status
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', py: 2 }}>
              Invitation Status
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', py: 2 }}>
              Ended By
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', py: 2 }}>
              Ended At
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredStudents.map((student) => (
            <TableRow key={student.row_key} hover sx={{ borderBottom: '1px solid #eee' }}>
              <TableCell align="right" sx={{ py: 2 }}>
                <Tooltip title="View Awards">
                  <IconButton 
                    size="small" 
                    onClick={() => handleOpenAwardsDialog(student)}
                    disabled={!student.is_active || !student.student_pk}
                    sx={{ 
                      color: student.is_active ? '#137333' : 'text.disabled', 
                      p: 1, 
                      mr: 0.5 
                    }}
                  >
                    <Badge 
                      badgeContent={student.award_count > 0 ? student.award_count : null}
                      color="secondary"
                    >
                      <EmojiEventsIcon fontSize="small" />
                    </Badge>
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit Student">
                  <IconButton 
                    size="small" 
                    onClick={() => handleEdit(student)}
                    sx={{ 
                      color: 'text.secondary', 
                      p: 1, 
                      mr: 0.5 
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ py: 2 }}>
                <Box display="flex" alignItems="center">
                  <Avatar 
                    sx={{ 
                      bgcolor: getColorFromName(student.first_name), 
                      width: 40, 
                      height: 40, 
                      fontSize: '0.875rem',
                      mr: 2,
                      color: 'black'
                    }}
                  >
                    {(student.first_name?.[0] || '?')}{(student.last_name?.[0] || (student.email?.[0] || ''))}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {student.first_name} {student.last_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {student.email}
                    </Typography>
                  </Box>
                </Box>
              </TableCell>
              <TableCell sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 250 }}>
                  {getSubjectChips(student.subjects).map((subject, idx) => (
                    <Chip 
                      key={idx} 
                      label={subject} 
                      size="small"
                      variant="outlined"
                      color={getSubjectColor(subject)}
                      sx={{
                        fontWeight: 500,
                        borderWidth: 1.5,
                        borderColor: theme => theme.palette[getSubjectColor(subject)]?.main,
                        color: theme => theme.palette[getSubjectColor(subject)]?.main,
                        backgroundColor: 'white'
                      }}
                    />
                  ))}
                </Box>
              </TableCell>
              <TableCell sx={{ py: 2 }}>
                <Typography variant="body2">
                  {formatDate(student.joined_date)}
                </Typography>
              </TableCell>
              <TableCell sx={{ py: 2 }}>
                <Chip 
                  label={getStatusLabel(student)} 
                  size="small"
                  variant="outlined"
                  color={getStatusColor(student)}
                  sx={{ 
                    backgroundColor: getStatusColor(student) === 'warning' ? '#FFF3E0' : 
                                   getStatusColor(student) === 'success' ? '#E6F4EA' : '#F5F5F5',
                    color: getStatusColor(student) === 'warning' ? '#E65100' : 
                          getStatusColor(student) === 'success' ? '#137333' : '#666666',
                    fontWeight: 500,
                    px: 1.5
                  }}
                />
              </TableCell>
              <TableCell sx={{ py: 2 }}>
                <Chip 
                  label={getInvitationStatusLabel(student.invitation_status)} 
                  size="small"
                  variant="outlined"
                  color={getInvitationStatusColor(student.invitation_status)}
                  sx={{ 
                    backgroundColor: getInvitationStatusColor(student.invitation_status) === 'warning' ? '#FFF3E0' : 
                                   getInvitationStatusColor(student.invitation_status) === 'success' ? '#E6F4EA' : '#F5F5F5',
                    color: getInvitationStatusColor(student.invitation_status) === 'warning' ? '#E65100' : 
                          getInvitationStatusColor(student.invitation_status) === 'success' ? '#137333' : '#666666',
                    fontWeight: 500,
                    px: 1.5
                  }}
                />
              </TableCell>
              <TableCell sx={{ py: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {student.ended_by_email ? student.ended_by_email : '-'}
                </Typography>
              </TableCell>
              <TableCell sx={{ py: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {student.ended_at ? formatDate(student.ended_at) : '-'}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// Render student cards for mobile view
  const renderMobileCards = () => (
    <Box sx={{ '& > :not(style)': { mb: 2 } }}>
      {filteredStudents.map((student) => (
        <Paper key={student.row_key} elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Box display="flex" alignItems="flex-start" mb={1.5}>
            <Avatar 
              sx={{ 
                bgcolor: 'primary.main', 
                width: 40, 
                height: 40, 
                fontSize: '0.875rem',
                mr: 2,
                color: 'white',
                mt: 0.5
              }}
            >
              {(student.first_name?.[0] || '?')}{(student.last_name?.[0] || (student.email?.[0] || ''))}
            </Avatar>
            <Box flexGrow={1}>
              <Typography variant="subtitle1" fontWeight={500}>
                {student.first_name} {student.last_name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {student.email}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Chip 
                  label={getStatusLabel(student)} 
                  size="small"
                  color={getStatusColor(student)}
                  variant="outlined"
                  sx={{ mr: 1 }}
                />
                <Chip 
                  label={getInvitationStatusLabel(student.invitation_status)} 
                  size="small"
                  color={getInvitationStatusColor(student.invitation_status)}
                  variant="outlined"
                  sx={{ mr: 1 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  {formatDate(student.joined_date)}
                </Typography>
              </Box>
            </Box>
          </Box>
          
          {student.subjects && (
            <Box sx={{ mt: 1.5, mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                SUBJECTS
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {getSubjectChips(student.subjects).map((subject, idx) => (
                  <Chip 
                    key={idx} 
                    label={subject} 
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}
          
          {student.notes && (
            <Box sx={{ mt: 1.5, mb: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                NOTES
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {student.notes}
              </Typography>
            </Box>
          )}
          
          {!student.is_active && (
            <Box sx={{ mt: 1.5, mb: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                RELATIONSHIP ENDED
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {student.ended_by_email && (
                  <Typography variant="body2">
                    <strong>Ended by:</strong> {student.ended_by_email}
                  </Typography>
                )}
                {student.ended_at && (
                  <Typography variant="body2">
                    <strong>Ended at:</strong> {formatDate(student.ended_at)}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
            <Tooltip title={!student.student_pk ? 'Awards available after the student joins the platform' : (student.is_active ? "View Awards" : "Cannot view awards for inactive relationship")}>
              <IconButton 
                size="small" 
                onClick={() => handleOpenAwardsDialog(student)}
                disabled={!student.is_active || !student.student_pk}
                color="primary"
                sx={{ 
                  mr: 1,
                  opacity: student.is_active ? 1 : 0.5
                }}
              >
                <Badge 
              badgeContent={student.award_count > 0 ? student.award_count : null}
              color="secondary"
              max={99}
              overlap="circular"
              >
              <EmojiEventsIcon fontSize="small" />
            </Badge>  
              </IconButton>
            </Tooltip>
            <Tooltip title={student.is_active ? "Edit" : "Cannot edit inactive relationship"}>
              <IconButton 
                size="small" 
                onClick={() => handleEdit(student)}
                disabled={!student.is_active}
                sx={{ 
                  mr: 1,
                  opacity: student.is_active ? 1 : 0.5
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          </Paper>
      ))}
    </Box>
  );

  const FilterPanel = ({ variant = 'side' }) => {
    const isTop = variant === 'top';

    return (
    <Paper 
      elevation={2} 
      sx={{ ...teacherFilterPanelSx(isTop, 20), zIndex: isTop ? 'auto' : 1000 }}
    >
      <Box sx={teacherFilterHeaderSx(isTop)}>
        <Typography
          variant={isTop ? 'caption' : 'h6'}
          sx={teacherFilterTitleSx(isTop)}
        >
          {isTop ? 'Student filters' : 'Filters'}
        </Typography>
        <Button
          size="small"
          startIcon={<Clear />}
          onClick={clearFilters}
          disabled={!Object.values(filters).some(v => v !== '')}
          sx={teacherFilterClearButtonSx}
        >
          Clear All
        </Button>
      </Box>
      
      <Stack
        spacing={isTop ? 1.5 : 3}
        direction={isTop ? 'row' : 'column'}
        useFlexGap
        flexWrap={isTop ? 'wrap' : 'nowrap'}
        alignItems={isTop ? 'center' : 'stretch'}
        sx={{
          ...teacherFilterTopStackSx(isTop ? { md: 170, lg: 190 } : '100%'),
          '& .MuiTextField-root:last-child': {
            width: isTop ? { md: 220, lg: 260 } : '100%',
          },
        }}
      >
        <TextField 
          select 
          label="Academic Year" 
          size="small" 
          value={filters.year} 
          onChange={(e) => setFilters(prev => ({ ...prev, year: e.target.value }))}
          InputLabelProps={{ shrink: true }}
          SelectProps={{
            displayEmpty: true,
            renderValue: (value) => {
              if (value === '') return 'All';
              const selectedYear = academicYears.find(y => y.id === value);
              return selectedYear ? selectedYear.year : value;
            },
            MenuProps: {
              PaperProps: {
                sx: {
                  zIndex: 9999,
                  maxHeight: 300
                }
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              }
            }
          }}
          sx={teacherFilterFieldSx}
        >
          <MenuItem value="">All</MenuItem>
          {academicYears.map(y => (
            <MenuItem key={y.id} value={y.id}>{y.year}</MenuItem>
          ))}
        </TextField>
        
        <TextField 
          select 
          label="Subject" 
          size="small" 
          value={filters.subject} 
          onChange={(e) => setFilters(prev => ({ ...prev, subject: e.target.value }))}
          InputLabelProps={{ shrink: true }}
          SelectProps={{
            displayEmpty: true,
            renderValue: (value) => {
              if (value === '') return 'All';
              const selectedSubject = subjects.find(s => s.id === value);
              return selectedSubject ? selectedSubject.name : value;
            },
            MenuProps: {
              PaperProps: {
                sx: {
                  zIndex: 9999,
                  maxHeight: 300
                }
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              }
            }
          }}
          sx={teacherFilterFieldSx}
        >
          <MenuItem value="">All</MenuItem>
          {subjects.map(s => (
            <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
          ))}
        </TextField>
        
        <TextField 
          select 
          label="Status" 
          size="small" 
          value={filters.status} 
          onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
          InputLabelProps={{ shrink: true }}
          SelectProps={{
            displayEmpty: true,
            renderValue: (value) => {
              if (value === '') return 'All';
              if (value === 'active') return 'Active';
              if (value === 'inactive') return 'Inactive';
              return value;
            },
            MenuProps: {
              PaperProps: {
                sx: {
                  zIndex: 9999,
                  maxHeight: 300
                }
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              }
            }
          }}
          sx={teacherFilterFieldSx}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
        </TextField>
        
        <TextField 
          select 
          label="Invitation Status" 
          size="small" 
          value={filters.invitation_status} 
          onChange={(e) => setFilters(prev => ({ ...prev, invitation_status: e.target.value }))}
          InputLabelProps={{ shrink: true }}
          SelectProps={{
            displayEmpty: true,
            renderValue: (value) => {
              if (value === '') return 'All';
              if (value === 'not_invited') return 'Not Invited';
              if (value === 'awaiting') return 'Awaiting';
              if (value === 'yes') return 'Accepted';
              if (value === 'no') return 'Rejected';
              return value;
            },
            MenuProps: {
              PaperProps: {
                sx: {
                  zIndex: 9999,
                  maxHeight: 300
                }
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              }
            }
          }}
          sx={teacherFilterFieldSx}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="not_invited">Not Invited</MenuItem>
          <MenuItem value="awaiting">Awaiting</MenuItem>
          <MenuItem value="yes">Accepted</MenuItem>
          <MenuItem value="no">Rejected</MenuItem>
          <MenuItem value="exited">Exited</MenuItem>
        </TextField>
        
        <TextField 
          label="Name / Email" 
          size="small" 
          value={filters.name} 
          onChange={(e) => setFilters(prev => ({ ...prev, name: e.target.value }))}
          placeholder="Search by name or email"
          InputLabelProps={{ shrink: true }}
          sx={teacherFilterFieldSx}
        />
      </Stack>
    </Paper>
    );
  };

  const EmptyState = () => (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center',
      py: 8,
      textAlign: 'center'
    }}>
      <Box sx={{ 
        width: 80, 
        height: 80, 
        borderRadius: '50%', 
        backgroundColor: 'grey.100',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 3
      }}>
        <PersonIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
      </Box>
      <Typography variant="h6" sx={{ mb: 1, color: 'text.secondary' }}>
        {isParent ? 'No Children Yet' : 'No Students Yet'}
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
        You haven't added any {isParent ? 'children' : 'students'} yet. Get started by adding your first {isParent ? 'child' : 'student'}.
      </Typography>
    </Box>
  );

  return (
    <>
      {/* Mobile Filter Drawer */}
      <Drawer
        anchor="left"
        open={mobileFilterOpen}
        onClose={() => setMobileFilterOpen(false)}
        sx={{ display: { md: 'none' } }}
      >
        <Box sx={{ width: 280, p: 2 }}>
          <FilterPanel />
        </Box>
      </Drawer>

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, minWidth: 0 }}>
        {/* Desktop Layout */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <FilterPanel variant="top" />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Paper 
              elevation={2} 
              sx={{ 
                p: { md: 2, lg: 3 },
                borderRadius: 3,
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                minWidth: 0
              }}
            >
              {/* Header */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'flex-start', 
                mb: 3 
              }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
                    {isParent ? 'Children' : 'Students'}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    Total {isParent ? 'Children' : 'Students'}: {filteredStudents.length}
                  </Typography>
                </Box>
                
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenDialog}
                  sx={{ 
                    borderRadius: 2,
                    textTransform: 'none',
                    px: 3,
                    py: 1,
                    fontWeight: 600,
                    boxShadow: 2,
                    '&:hover': {
                      boxShadow: 4,
                      transform: 'translateY(-1px)',
                      transition: 'all 0.2s'
                    }
                  }}
                >
                  {isParent ? 'Add Child' : 'Add Student'}
                </Button>
              </Box>

              {/* Error Alert */}
              {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                  {error}
                </Alert>
              )}

              {/* Content */}
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                  <CircularProgress />
                </Box>
              ) : filteredStudents.length === 0 ? (
                <EmptyState />
              ) : (
                <Box>
                  {/* Desktop Table */}
                  <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                    {renderDesktopRows()}
                  </Box>
                  
                  {/* Mobile Cards */}
                  <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                    {renderMobileCards()}
                  </Box>
                </Box>
              )}
            </Paper>
          </Box>
        </Box>

        {/* Mobile Layout */}
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          <Paper 
            elevation={2} 
            sx={{ 
              p: 2, 
              borderRadius: 3,
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <SummaryCard 
              label={isParent ? 'Total Children' : 'Total Students'} 
              value={filteredStudents.length} 
              icon={PersonIcon} 
            />

            {/* Horizontally scrollable action bar */}
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                overflowX: 'auto',
                pb: 1,
                mb: 2,
                flexShrink: 0,
                '&::-webkit-scrollbar': { height: 6 },
              }}
            >
              <Button
                variant="outlined"
                size="small"
                startIcon={<FilterListIcon />}
                onClick={() => setMobileFilterOpen(true)}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, flexShrink: 0 }}
              >
                Filters
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleOpenDialog}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, flexShrink: 0 }}
              >
                {isParent ? 'Add Child' : 'Add Student'}
              </Button>
            </Box>

            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            {/* Mobile Content */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : filteredStudents.length === 0 ? (
              <EmptyState />
            ) : (
              <Box>
                {renderMobileCards()}
              </Box>
            )}
          </Paper>
        </Box>
      </Box>

      {/* Awards Dialog */}
      <Dialog 
        open={awardsDialogOpen} 
        onClose={handleCloseAwardsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedStudentForAwards ? `${selectedStudentForAwards.first_name}'s Awards` : 'Awards'}
        </DialogTitle>
        <DialogContent>
          {selectedStudentForAwards && (
            <StudentAwardsTab 
              studentId={selectedStudentForAwards.student_pk} 
              teacherId={user?.id}
              managedStudentId={selectedStudentForAwards.managed_student_id}
              studentName={`${selectedStudentForAwards.first_name} ${selectedStudentForAwards.last_name}`}
              onClose={handleCloseAwardsDialog}
              onUpdated={fetchStudents}
              debug={true}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Add/Edit Student Dialog */}
      <AddStudentDialog
        open={openDialog}
        onClose={handleCloseDialog}
        editingStudent={editingStudent}
        onSuccess={fetchStudents}
        showSnackbar={showSnackbar}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          elevation={6}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default StudentsPage;
